"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        <div className="h-6 w-20 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-6 w-20 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-6 w-20 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-mono text-muted-foreground transition-all duration-300 hover:text-primary text-sm font-medium tracking-wider uppercase">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background border-b border-border p-6 flex flex-col gap-6 md:hidden shadow-xl origin-top animate-in slide-in-from-top-2 fade-in duration-200">
      {loading ? <div className="flex flex-col gap-4">
          <div className="h-8 w-32 bg-muted/20 animate-pulse rounded-md" />
          <div className="h-8 w-32 bg-muted/20 animate-pulse rounded-md" />
        </div> : <nav className="flex flex-col gap-4">
          {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="font-mono text-foreground hover:text-primary text-lg font-medium py-2 border-b border-border transition-colors" onClick={() => setIsOpen(false)}>
              {item.label}
            </Link>)}
        </nav>}
      <Link to="/StartAProjectPage" onClick={() => setIsOpen(false)} className="mt-4">
        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base font-bold rounded-md shadow-md shadow-primary/10">
          Start a Project
        </Button>
      </Link>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="259823" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-[12px] border-b border-border shadow-md" : "bg-background border-b border-transparent")}>
      <div className="container mx-auto px-4 md:px-6 h-20 md:h-24 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 transition-transform hover:scale-105 duration-300">
          <WvcLogo className="h-8 md:h-10 w-auto text-primary" />
        </Link>

        {}
        <MenuProvider menu_id="45">
          <DesktopMenu />
        </MenuProvider>

        {}
        <div className="flex items-center gap-4">
          <Link to="/StartAProjectPage" className="hidden md:block">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 font-default font-bold px-6 rounded-md border border-primary/50">
              Start a Project
            </Button>
          </Link>
          
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent/20 rounded-md" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      <MenuProvider menu_id="45">
        <MobileMenu isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </section>;
}